// src/contexts/controleBar/comandas/ComandaContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchData, postData, putData, deleteData } from '../../../services/Api';
import { useAlert } from '../../geral/AlertContext';
import { useMessage } from '../../geral/MessageContext';
import { useMesa } from '../mesas/MesaContext';
import { useSSE } from '../../geral/SSEContext';

const ComandaContext = createContext();
export const useComanda = () => useContext(ComandaContext);

export const ComandaProvider = ({ children }) => {
    const [comandaSel, setComandaSel] = useState();
    const [comandasAtivas, setComandasAtivas] = useState([]);
    const [comandasByMesa, setComandasByMesa] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();
    const { mesaSel, setMesaSel} = useMesa(); 
    const { subscribeToEvent } = useSSE();

    const fetchComandas = useCallback(async (mesaId) => {
        try {
            if (!mesaId){
                setComandasByMesa([]); 
                return [];
            } else {
                const result = await fetchData('/Comanda/GetComandasByMesa', { mesaId });
                setComandasByMesa(result || []);  
                return result || [];
            }
            
        } catch (error) {
            console.error("Erro ao buscar comandas:", error);
            showMessage(`Erro ao buscar comandas! ${error?.message || error}`, 'danger');
        }
    }, []);

    useEffect(() => {
        fetchComandas();
    }, [fetchComandas]);

    const fetchComandasAtivas = useCallback(async () => {
        try {

            const result = await fetchData('/Comanda/GetComandasAtivas');
            setComandasAtivas(result || []);
            return result || [];
        
        } catch (error) {
            console.error("Erro ao buscar comandas:", error);
            showMessage(
            `Erro ao buscar comandas! ${error?.message || error}`,
            'danger'
            );
        }
    }, []);

    useEffect(() => {
        return subscribeToEvent('controlebar-comanda', async (event) => {
            let dadosEvento = null;

            try {
                dadosEvento = event?.data ? JSON.parse(event.data) : null;
            } catch {
                console.warn('Evento de comanda recebido sem dados JSON:', event?.data);
            }

            const mesaIdAtual = mesaSel?.id;
            const mesaIdEvento = dadosEvento?.mesaId;
            const atualizacoes = [fetchComandasAtivas()];

            if (
                mesaIdAtual &&
                (!mesaIdEvento || Number(mesaIdEvento) === Number(mesaIdAtual))
            ) {
                atualizacoes.push(fetchComandas(mesaIdAtual));
            }

            await Promise.all(atualizacoes);
        });
    }, [fetchComandas, fetchComandasAtivas, mesaSel?.id, subscribeToEvent]);

    const addComanda = async (comanda) => {
        try {
            const result = await postData('/Comanda', comanda);
            setComandasByMesa(prev => [...prev, result]);
            setComandasAtivas(prev => [...prev, result]);
            //coloca o id na mesaSel
            setMesaSel({...mesaSel, id:result.mesaId});
            showAlert('Comanda adicionada com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao adicionar comanda:", error);
            showMessage('Erro ao adicionar comanda!\n' + error, 'danger');
        }
    };

    const updateComanda = async (comanda) => {
        try {
            const result = await putData('/Comanda', comanda);
            setComandasByMesa(prev =>
                prev.map(c => (c.id === result.id ? result : c))
            );
            setComandasAtivas(prev =>
                prev.map(c => (c.id === result.id ? result : c))
            );
            showAlert('Comanda alterada com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao atualizar comanda:", error);
            showAlert('Erro ao alterar comanda!', 'danger');
        }
    };

    const getDeleteComandaErrorMessage = (error) => {
        const data = error?.data;

        if (typeof data === 'string' && data.trim()) {
            return data;
        }

        return data?.message || data?.title || error?.message || 'Erro ao excluir comanda!';
    };

    const deleteComanda = async (id) => {
        try {
            await deleteData('/Comanda', { id });
            setComandasByMesa(prev => prev.filter(c => c.id !== id));
            setComandasAtivas(prev => prev.filter(c => c.id !== id));
            showAlert('Comanda excluída com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir comanda:", error);
            showAlert(getDeleteComandaErrorMessage(error), 'danger');            
        }
    };

    return (
        <ComandaContext.Provider value={{ comandasAtivas, setComandasAtivas, comandasByMesa, setComandasByMesa, comandaSel, setComandaSel, 
            addComanda, updateComanda, deleteComanda, 
            fetchComandas, fetchComandasAtivas }}>
            {children}
        </ComandaContext.Provider>
    );
};
